import { booleanAttribute } from "#ce/utils/props";
import { props as textFieldProps } from "#ce/components/SpTextField/props.js";

export const inputmodeOptions = ["none", "text", "decimal", "numeric", "tel", "search", "email", "url"];
export const appearanceOptions = ["default", "inline", "text"];

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
// `value` is absent on purpose and is documented in the story: the element owns a live `value`
// property, which a declared prop would shadow with an accessor onto the prop store. The `value`
// attribute seeds the selection and names what a form reset restores.
export const props = Object.freeze({
  /**
   * Placeholder text rendered in the activator when no value is selected.
   *
   * @type {String}
   * @default undefined
   */
  placeholder: {
    type: String,
    default: undefined,
  },
  /**
   * List of items to display in the dropdown. Each item is either a string (used for both title and value) or an object whose `title`/`value` keys are read by default (override via `itemTitle`/`itemValue`).
   *
   * @type {Array<String|Object>}
   * @default []
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Property name on item objects that holds the title.
   *
   * @type {String}
   * @default "title"
   */
  itemTitle: {
    type: String,
    default: "title",
  },
  /**
   * Property name on item objects that holds the value.
   *
   * @type {String}
   * @default "value"
   */
  itemValue: {
    type: String,
    default: "value",
  },
  /**
   * Disable the select. Prevents clicks and focus on the activator.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * `inputmode` attribute for the underlying text field. Use "text" (or another non-"none" value) to enable typing/search in the activator.
   *
   * @type {String}
   * @default "none"
   * @values none, text, decimal, numeric, tel, search, email, url
   * @storybook-control select
   */
  inputmode: {
    type: String,
    default: "none",
    validator: (value) => inputmodeOptions.includes(value),
  },
  /**
   * Show the quantum-select in-list search bar at the top of the dropdown, so long option lists can be filtered. Filtering is handled internally (client-side) — no `text-input` wiring needed, unlike the `inputmode` approach where the consumer filters `items` itself.
   *
   * @type {Boolean|String}
   * @default false
   */
  searchable: booleanAttribute(),
  /**
   * Switch the in-list search from substring matching to typo-tolerant fuzzy matching with relevance ordering ("kardiology" finds "Cardiology"). Forwarded to the inner quantum-select; only meaningful together with `searchable`.
   *
   * @type {Boolean|String}
   * @default false
   */
  fuzzySearch: booleanAttribute(),
  /**
   * Maximum number of matched items rendered for a search query, forwarded to the inner quantum-select. Undefined (the default) renders every match. Set this when binding a large item set so a broad query can't render thousands of rows synchronously. Idle (no-query) browsing is unaffected.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  maxResults: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Allow multiple selections. When `true`, the value becomes an array.
   *
   * @type {Boolean}
   * @default false
   */
  multiple: {
    type: Boolean,
    default: false,
  },
  /**
   * Enforce a required selection: once a value is chosen it cannot be toggled
   * off by re-selecting it, so the select always keeps a selected value.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * name attribute used for the element's form value — required to participate in a native form submit.
   *
   * @type {String}
   * @default undefined
   */
  name: {
    type: String,
    default: undefined,
  },
  /**
   * Show a loading indicator inside the activator.
   *
   * @type {Boolean|String}
   * @default false
   */
  loading: booleanAttribute(),
  /**
   * Keep the dropdown menu open after a selection instead of closing it. Useful with `multiple`, where picking several options otherwise means reopening the menu per pick. Outside clicks and the menu's own close paths still dismiss it.
   *
   * @type {Boolean|String}
   * @default false
   */
  keepOpenOnSelect: booleanAttribute(),
  /**
   * Return the full item object on selection instead of the value pointed at by `itemValue`.
   *
   * @type {Boolean}
   * @default false
   */
  returnObject: {
    type: Boolean,
    default: false,
  },
  /**
   * Hide the "no data" message shown when there are no matching items.
   *
   * @type {Boolean}
   * @default false
   */
  hideNoData: {
    type: Boolean,
    default: false,
  },
  /**
   * Text shown when no items match the current search.
   *
   * @type {String}
   * @default "No data"
   */
  noDataText: {
    type: String,
    default: "No data",
  },
  /**
   * Configuration object forwarded to the dropdown menu.
   *
   * @type {Object}
   * @default {}
   */
  menu: {
    type: [Object, Boolean, String],
    default: () => ({}),
  },
  /**
   * Prefix string prepended to the rendered selected value. Useful for adding a label like "Sort by: " in front of the selection.
   *
   * @type {String}
   * @default undefined
   */
  prependSelectedWith: {
    type: String,
    default: undefined,
  },
  /**
   * Add a null-value option at the top of the items list, e.g. "Select one…".
   *
   * @type {String}
   * @default undefined
   */
  prompt: {
    type: String,
    default: undefined,
  },
  /**
   * Label rendered above the select input.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Density (vertical height) of the text-field activator.
   *
   * @type {String}
   * @default "default"
   * @values default, compact, comfortable
   * @storybook-control select
   */
  density: textFieldProps.density,
  /**
   * Visual variant of the text-field activator. "plain" renders a borderless, padding-free activator for minimal inline contexts such as toolbar and composer footers.
   *
   * @type {String}
   * @default "default"
   * @values default, outlined, plain
   * @storybook-control select
   */
  variant: textFieldProps.variant,
  /**
   * Visual appearance of the select. "default" is the standard text-field activator. "inline" keeps that activator and sizes the dropdown to its own content rather than to the activator's width. "text" additionally renders the activator as a quiet run of text — borderless, compact, hugging the selected label, at the surrounding font size — for a toolbar or composer footer.
   *
   * @type {String}
   * @default "default"
   * @values default, inline, text
   * @storybook-control select
   */
  appearance: {
    type: String,
    default: "default",
    validator: (value) => appearanceOptions.includes(value),
  },
  /**
   * Caret glyph style on the activator. "outlined" is a thin chevron; "filled" is a solid caret; "double" renders the up/down pair used for ASC/DESC controls.
   *
   * @type {String}
   * @default "outlined"
   * @values outlined, filled, double
   * @storybook-control select
   */
  caretVariant: {
    type: String,
    default: "outlined",
    validator: (value) => ["outlined", "filled", "double"].includes(value),
  },
  /**
   * Show a pointer arrow on the dropdown menu, anchored at the activator.
   *
   * @type {Boolean|String}
   * @default false
   */
  pointer: booleanAttribute(),
  /**
   * Allow list item text to wrap onto multiple lines when it overflows the available width, instead of being clipped to a single line.
   *
   * @type {Boolean|String}
   * @default false
   */
  wrap: booleanAttribute(),
});
